/**
 * O registry PARCIAL da Gestão de Tarefas — spec-010 do
 * `project-plataforma-por-chat`, [T016] / US-2.
 *
 * Dois cartões que o módulo já desenhava na tela passam a ser desenháveis
 * DENTRO da conversa, sem que o `chat-message.vue` saiba que a Gestão de
 * Tarefas existe: ele resolve a chave que veio em `ui.component` no
 * `UI_REGISTRY` e monta. Cartão novo do módulo é uma entrada aqui — nunca uma
 * linha na bolha.
 *
 * ## Como cada peça se encaixa
 *
 * | peça | onde mora | por quê |
 * |---|---|---|
 * | `component` | `ia/cartoes/*.js` | embrulho que traduz o `ui.props` em props |
 * | `schema` | `ui-registry/schemas/` | CÓPIA byte a byte do `contratos/` do backend |
 *
 * ★ O `component` NÃO aponta para o `.vue`. Aponta para o embrulho, porque o
 * fio é PT-BR/snake_case (`titulo`, `due_date`, `listas`) e os componentes
 * reusados falam camelCase/inglês — ver `ia/adaptadores.js`. Apontar direto
 * deixaria o cartão montado e VAZIO, que é a falha calada que o contrato de UI
 * existe para evitar.
 *
 * ★ O `schema` valida o `ui.props` CRU (é ele que o `chat-message` passa a
 * `validarProps`), e por isso é o contrato do backend sem uma vírgula de
 * diferença. A junção `contrato-ui-schemas.unit.js` compara os dois lados byte
 * a byte.
 *
 * ## Nenhuma das duas tools entra no `LEGACY_TOOL_TO_UI`
 *
 * O mapa legado existe para a mensagem persistida ANTES de a tool declarar
 * `ui`, e para a tool que ainda não declara. `list_tarefas` e
 * `list_pastas_tarefas` já publicam `ui.component` ([T007] e [T008]), e o
 * `ui.component` tem precedência sobre o mapa. Uma entrada lá seria uma segunda
 * fonte do mesmo fato, e a divergência entre as duas é calada.
 *
 * ## Os `states` são uma decisão, não uma cópia
 *
 * `input-available` entra nos dois: é o estado em que a parte vive enquanto a
 * tool roda (e enquanto o veredito do schema não volta), e recusá-lo faria
 * aparecer um cartão de ERRO no meio de todo turno normal, trocado pelo cartão
 * certo um segundo depois — por isso os dois desenham esqueleto nesse estado.
 * `output-error` e `output-denied` ficam de fora: nenhum dos dois sabe desenhar
 * uma falha, e um cartão que renderiza nada é pior que o `fallback-card`
 * dizendo o que aconteceu. `approval-requested` não se aplica: são leituras.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { FEATURES } from '@constants/Text'
import { ROUTE_KEYS } from '@constants/navigation-routes'

export { ROTA_DO_BOARD } from './acoes'

/** A chave do módulo no catálogo de navegação (`ROUTE_KEYS.TASK` → rota `task`). */
const MODULO = ROUTE_KEYS.TASK

/**
 * A flag do MÓDULO (`FEATURES.TASK_V2`, "Gerenciador de tarefas") — a mesma que
 * gateia a rota (`navigation-routes.js`) e o `PERMISSION` do módulo.
 *
 * Sem ela o `resolverEntrada` recusa por `flag_desligada`, que é fail-closed de
 * propósito: um cartão de tarefas dentro da conversa de um escritório que não
 * contratou o módulo mostraria dado de coisa que ele não tem, e isso não tem
 * desfazer.
 */
const FLAG_DO_MODULO = FEATURES.TASK_V2

// Enquanto a tool roda, e quando o resultado chega (ver o cabeçalho).
const ESTADOS_DE_LEITURA = ['input-available', 'output-available']

/**
 * As chaves, no formato `<modulo>/<cartao>` do contrato.
 *
 * Exportadas porque quem lê o registry e quem escreve o teste precisam da MESMA
 * string: uma chave redigitada não quebra nada visivelmente — o cartão
 * simplesmente não aparece, e o turno segue como se a tool não tivesse
 * desenhado nada.
 */
export const CHAVES = {
  LISTA: 'tarefas/lista',
  PASTAS_LISTAS: 'tarefas/pastas-listas',
}

export default {
  [CHAVES.LISTA]: {
    component: () => import('@modules/tarefas/ia/cartoes/lista'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-tarefas-lista.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando tarefas...',
  },
  [CHAVES.PASTAS_LISTAS]: {
    component: () => import('@modules/tarefas/ia/cartoes/pastas-listas'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-tarefas-pastas-listas.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando pastas e listas...',
  },
}
